import React, { useState, useEffect, useCallback } from 'react';
import { FileText, CheckCircle, X, Calendar, RefreshCw, Layers, Link as LinkIcon, Hash } from 'lucide-react';
import { apiService } from '../api/apiclient';
import PostCard from '../components/PostCard';
import PostDetailPanel from '../components/PostDetailPanel';
import TimeRangeFilter from "../components/TimeRangeFilter";

const POST_PER_PAGE = 6;

const PostListPage = () => {
    const [activeTab, setActiveTab] = useState('published');
    const [posts, setPosts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [page, setPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);
    const [selectedDetail, setSelectedDetail] = useState(null);
    const [timeFilter, setTimeFilter] = useState({ type: 'preset', value: 'all' });

    // --- 辅助解析函数（用于解析 DB 里的 JSON 字符串和分号分隔符） ---
    const parseSafeJSON = (str, fallback = []) => {
        try { return typeof str === 'string' ? JSON.parse(str) : (str || fallback); }
        catch (e) { return fallback; }
    };

    const getStatusQuery = (tab) => {
        const map = { published: 'published', draft: 'draft' };
        return map[tab] || 'all';
    };

    useEffect(() => {
        setPage(1);
        setSelectedDetail(null);
        fetchPosts(1, true);
    }, [activeTab, timeFilter]);

    useEffect(() => {
        if (page > 1) {
            fetchPosts(page, false);
        }
    }, [page]);

    const handleLoadMore = () => {
        if (!loading && hasMore) {
            setPage(prev => prev + 1);
        }
    };

    const fetchPosts = useCallback(async (pageNum, isNewTab = false) => {
        if (loading) return;
        setLoading(true);
        try {
            const status = getStatusQuery(activeTab);
            let timeRange = null;
            if (timeFilter.type === 'preset') {
                if (timeFilter.value !== 'all') {
                    const offsets = { '1d': 86400000, '7d': 604800000, '30d': 2592000000 };
                    timeRange = {
                        start: Date.now() - (offsets[timeFilter.value] || 0),
                        end: Date.now()
                    };
                }
            } else {
                timeRange = {
                    start: new Date(timeFilter.value.start + " 00:00:00").getTime(),
                    end: new Date(timeFilter.value.end + " 23:59:59").getTime()
                };
            }

            const response = await apiService.listPosts(status, pageNum, POST_PER_PAGE, timeRange);
            const newPosts = response.posts || [];

            setPosts(prev => isNewTab ? newPosts : [...prev, ...newPosts]);
            setHasMore(newPosts.length === POST_PER_PAGE);
        } catch (error) {
            console.error("Failed to load posts:", error);
        } finally {
            setLoading(false);
        }
    }, [activeTab, loading, timeFilter]);

    const removePostFromList = (postId) => {
        setPosts(prev => prev.filter(p => p.id !== postId));
    };

    return (
        <div className="p-4 md:p-8 max-w-[1600px] mx-auto h-screen flex flex-col bg-gray-50/20">
            {/* Header 部分 */}
            <div className="flex flex-col lg:flex-row justify-between lg:items-center mb-8 shrink-0 gap-4">
                <div className="flex flex-col gap-3">
                    <h1 className="text-2xl md:text-3xl font-bold text-gray-900 tracking-tight">Publishing Management</h1>
                    <TimeRangeFilter onFilterChange={(filter) => setTimeFilter(filter)} />
                </div>
                <div className="flex bg-gray-100 p-1 rounded-xl self-start">
                    {[
                        { id: 'draft', label: 'Draft', icon: <FileText size={16} /> },
                        { id: 'published', label: 'Published', icon: <CheckCircle size={16} /> },
                    ].map(tab => (
                        <button
                            key={tab.id}
                            onClick={() => setActiveTab(tab.id)}
                            className={`flex items-center gap-2 px-4 md:px-6 py-2 rounded-lg text-sm font-bold transition ${activeTab === tab.id ? 'bg-white text-red-600 shadow-sm' : 'text-gray-500 hover:text-gray-700'}`}
                        >
                            {tab.icon} <span className="hidden sm:inline">{tab.label}</span>
                        </button>
                    ))}
                </div>
            </div>

            <div className="flex flex-1 gap-6 overflow-hidden relative">
                {/* 左侧列表 */}
                <div className="w-full xl:w-1/2 overflow-y-auto pr-2 flex flex-col gap-4 custom-scrollbar">
                    {posts.map(post => (
                        <PostCard
                            key={post.id}
                            post={post}
                            activeTab={activeTab}
                            selectedDetail={selectedDetail}
                            setSelectedDetail={setSelectedDetail}
                            onDeleteSuccess={removePostFromList}
                        />
                    ))}
                    {posts.length > 0 && (
                        <div className="py-8 flex justify-center">
                            {hasMore ? (
                                <button
                                    onClick={handleLoadMore}
                                    disabled={loading}
                                    className="px-8 py-3 rounded-xl text-sm font-bold transition-all bg-white text-red-600 border border-red-100 hover:bg-red-50 hover:shadow-md active:scale-95"
                                >
                                    {loading ? 'Loading...' : 'Load More Posts'}
                                </button>
                            ) : (
                                <div className="flex flex-col items-center gap-2 text-gray-400 py-4">
                                    <div className="h-px w-12 bg-gray-200" />
                                    <p className="text-xs font-medium uppercase tracking-widest">No more posts to show</p>
                                </div>
                            )}
                        </div>
                    )}

                    {!loading && posts.length === 0 && (
                        <div className="h-64 flex flex-col items-center justify-center text-gray-400 border-2 border-dashed border-gray-100 rounded-3xl">
                            <p className="text-sm">No posts found in this period.</p>
                        </div>
                    )}
                </div>

                {/* ✨ 右侧详情重构：全渠道元数据饱满展示 */}
                <div className={`
                    ${(selectedDetail) ? 'flex' : 'hidden xl:flex'} 
                    flex-1 overflow-hidden bg-white rounded-2xl border border-gray-100 
                    fixed inset-y-4 right-4 left-4 z-50 shadow-2xl 
                    xl:relative xl:inset-0 xl:z-0 xl:shadow-none
                    transition-all duration-300 transform
                `}>

                    {(selectedDetail) && (
                        <button
                            onClick={() => { setSelectedDetail(null); }}
                            className="xl:hidden absolute top-4 right-4 z-[60] p-2 bg-gray-100 rounded-full hover:bg-gray-200"
                        >
                            <X size={20} className="text-gray-600" />
                        </button>
                    )}

                    {selectedDetail && <PostDetailPanel postId={selectedDetail.id} />}
                </div>

                {/* 遮罩层 */}
                {(selectedDetail) && (
                    <div
                        className="xl:hidden fixed inset-0 bg-slate-900/20 backdrop-blur-sm z-40 animate-in fade-in"
                        onClick={() => { setSelectedDetail(null); }}
                    />
                )}
            </div>
        </div>
    );
};

export default PostListPage;